import { t } from 'translate/i18n'
import { Box, Grid, Typography } from '@material-ui/core'
import Chip from '@material-ui/core/Chip'
import clsx from 'clsx'
import React, { ComponentProps, useMemo, useState } from 'react'
import useStyles from './CollectionItem.style'
import { currencyNumberFormatter } from 'core/helpers/formatter'
import AssetDisplay from 'openspace/components/AssetDisplay'
import {
  CollectionItemType,
  CollectionItemViewProps,
} from './CollectionItem.types'
import Countdown from '@onepercentio/one-ui/components/Countdown'
import Text from 'openspace/components/Text'
import useHero from '@onepercentio/one-ui/hooks/useHero'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider'
import { PendingPurchase } from 'core/logic/purchase/purchase.types'
import InfoModalView from 'components/InfoModal/InfoModal.view'
import ActionButtonView from 'components/ActionButton/ActionButton.view'
import BackButtonView from 'components/BackButton/BackButton.view'
import { AssetType } from 'core/logic/asset/asset.types'
import { TrackingStatusLabel } from 'openspace/pages/Authenticated/Tracking/Tracking.view'
import Tag from 'components/Tag'
import { SHOW_DESCRIPTION } from './CollectionItem.data'
import { NAME_DISPLAY_MODEL } from './CollectionItem.data'
import PingPongText from '@onepercentio/one-ui/components/PingPongText'
import SCSS from './CollectionItem.module.scss'
import OfferPriceLogic from 'components/offer/OfferPrice/OfferPrice.logic'
import { CurrenciesEnum } from 'core/types/CurrenciesEnum'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import { DELIVERY_STATUSES } from 'context/Delivery'

const Actions = ({
  onContinue,
  onBack,
}: {
  onContinue: () => void
  onBack: () => void
}) => {
  return (
    <div style={{ display: 'flex' }}>
      <ActionButtonView
        text={t('authenticated.myCollection.open')}
        onClick={onContinue}
      />
      <BackButtonView
        icon='close'
        text={t('authenticated.myCollection.cancel')}
        onClick={onBack}
      />
    </div>
  )
}

const CollectionItemView: React.FC<CollectionItemViewProps> = ({
  id,
  onClick,
  itemName,
  itemCount,
  itemAnimation,
  orientation,
  itemDescription,
  offerDetails,
  asset,
  expiration,
  ...props
}) => {
  const { type } = props
  const classes = useStyles()

  const heroId =
    props.type === CollectionItemType.DELIVERY
      ? props.delivery.id
      : props.type === CollectionItemType.PURCHASE
        ? props.purchase.id
        : undefined

  const truncateString = (string?: string) => {
    return string ? string.replace(/(.{150})..+/, '$1...') : ''
  }

  const timeRemaining = useMemo(() => {
    if (expiration) return expiration.getTime() - Date.now()
  }, [expiration])

  const { heroRef } = useHero(
    `collection-item-${asset?.id}-${asset?.tokenId ?? 'default'}-${heroId}`,
    undefined,
    {
      onHeroSkip: () => false,
      onHeroDetect: () => {
        return !document.querySelectorAll(
          '[data-orderableid="orderable-list-clone"]'
        ).length
      },
    }
  )

  const [selectedDelivery, setSelectedDelivery] = useState(false)

  const _onClickItem = () => {
    if (onClick) {
      switch (type) {
        case CollectionItemType.DELIVERY:
          if (DELIVERY_STATUSES.includes(props.delivery.status))
            onClick(id || '')
          else setSelectedDelivery(true)
          break
        default:
          onClick(id || '')
      }
    }
  }

  const assetToDisplay = useMemo<
    ComponentProps<typeof AssetDisplay>['asset']
  >(() => {
    if (props.type === CollectionItemType.DELIVERY) {
      if (props.delivery.type === 'airdrop') {
        const asset = props.delivery.asset!
        return {
          id: asset.id,
          tokenId: asset.tokenId,
          media: asset.media,
        }
      } else if (props.delivery.offer) {
        const offer = props.delivery.offer
        return {
          id: offer.id,
          tokenId: 0,
          media: { coverUrl: offer?.media.icon ?? offer?.image } as AssetType['media'],
        }
      } else {
        const assetToConsider = props.delivery.asset || asset!
        return {
          id: assetToConsider.id,
          tokenId: assetToConsider.tokenId ?? 0,
          media: {
            coverUrl: assetToConsider.media.imageUrl,
          } as AssetType['media'],
        }
      }
    } else if (props.type === CollectionItemType.PURCHASE) {
      const offer = props.purchase.offer
      if (offer)
        return {
          id: offer.id,
          tokenId: 0,
          media: { coverUrl: offer?.media.icon ?? offer?.image } as AssetType['media'],
        }
      else return asset
    } else {
      return asset!
    }
  }, [props.type, asset])

  return (
    <>
      <Box
        {...({
          ref: heroRef,
        } as any)}
        display='flex'
        flexDirection='column'
        className={clsx(
          classes.container,
          clsx(
            classes.pointer,
            orientation === 'vertical'
              ? classes.containerVertical
              : classes.containerHorizontal
          )
        )}
        onClick={!itemAnimation ? _onClickItem : undefined}
        data-testid={props['data-testid']}>
        <Grid container item className={SCSS.imgContainer}>
          <AssetDisplay asset={assetToDisplay} uniqueId={heroId} />
          {offerDetails && (
            <Tag className={SCSS.onSaleBadge} color='primary'>
              {t('authenticated.myCollection.collectionItem.active')}
            </Tag>
          )}
        </Grid>
        <Grid
          container
          item
          className={classes.descriptionContainer}
          onClick={
            onClick
              ? (e: React.MouseEvent) => {
                e.stopPropagation()
                _onClickItem()
              }
              : undefined
          }>
          <Box
            display={'flex'}
            className={clsx(
              orientation === 'horizontal'
                ? classes.descriptionBoxPadding
                : null,
              classes.descriptionBox
            )}>
            {NAME_DISPLAY_MODEL === 'inline' ? (
              <PingPongText type='p12' className={classes.inlineText}>
                {itemName}
              </PingPongText>
            ) : (
              <Typography variant='h4' color='textPrimary'>
                {itemName}
              </Typography>
            )}
          </Box>

          {SHOW_DESCRIPTION && (
            <Typography
              variant='body2'
              color='textSecondary'
              className={classes.description}>
              {truncateString(itemDescription)}
            </Typography>
          )}
          {itemCount && (
            <Box>
              <Chip
                label={t('authenticated.myCollection.collectionItem.unitsNew', {
                  count: offerDetails?.quantity || itemCount,
                })}
                color='primary'
                className={classes.chip}
              />
            </Box>
          )}
          {expiration && (
            <Box>
              <Chip
                label={
                  <>
                    {t('authenticated.myCollection.timeLeft')}{' '}
                    <Countdown
                      timeRemaining={timeRemaining!}
                      options={{ hours: false }}
                    />
                  </>
                }
                color='primary'
                className={classes.chip}
              />
            </Box>
          )}
          {offerDetails && (
            <div className={SCSS.offerResume}>
              <Text
                code='authenticated.myCollection.collectionItem.announcePrice'
                type={LPTypo.P1}
              />
              <Text type={LPTypo.P1Bold}>
                <OfferPriceLogic
                  prices={{
                    [offerDetails.currency]: offerDetails.price,
                  }}
                  preferredCurrency={offerDetails.currency as CurrenciesEnum}
                  mode='text'
                />
              </Text>
            </div>
          )}
          {type === CollectionItemType.DELIVERY && (
            <Text type={FigmaTypo.P12}>
              {DELIVERY_STATUSES.includes(props.delivery.status) ? (
                <Tag variant='outline' size='small'>
                  <TrackingStatusLabel
                    status={
                      props.delivery.logistics?.events?.at(-1)?.status ??
                      'PAYED'
                    }
                  />
                </Tag>
              ) : props.delivery.type === 'airdrop' ? (
                t('authenticated.myCollection.clickToClaim')
              ) : (
                t('authenticated.myCollection.clickToOpen')
              )}
            </Text>
          )}
          {type === CollectionItemType.PURCHASE && (
            <CountdownLabel purchase={props.purchase} />
          )}
        </Grid>
      </Box>
      <InfoModalView
        title={t('authenticated.myCollection.open')}
        open={selectedDelivery}
        handleClose={() => setSelectedDelivery(false)}
        content={
          type === CollectionItemType.DELIVERY &&
            props.delivery.type === 'airdrop'
            ? t('authenticated.myCollection.wantToProceedWithClaim')
            : t('authenticated.myCollection.wantToProceed')
        }
        actions={
          <Actions
            onContinue={() => onClick!(id)}
            onBack={() => setSelectedDelivery(false)}
          />
        }
      />
    </>
  )
}

function CountdownLabel({ purchase }: { purchase: PendingPurchase }) {
  const timeRemaining = useMemo(() => {
    return purchase.expireAt.getTime() - Date.now()
  }, [purchase])

  return (
    <>
      <Text type={FigmaTypo.P12}>
        {t('authenticated.myCollection.proceed')}{' '}
      </Text>
      <Text type={FigmaTypo.P12}>
        {t('authenticated.myCollection.timeLeft')}{' '}
        <Countdown timeRemaining={timeRemaining!} options={{ hours: false }} />
      </Text>
    </>
  )
}

export default CollectionItemView
